import { useEffect, useContext, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { StoreContext } from '../../Store';
import DataTable from '../../DataTable';
import { NeeNorSelect } from '../../Input/Input';
import { getNewLoadNeeNorDropdown } from '../../apiCalls';
import warningIcon from '../../../images/warning.svg';
import greenCircleTick from '../../../images/green-circle-tick.png';
import redCircleCross from '../../../images/red-circle-cross.png';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import Tooltip from '../../Tooltip/Tooltip';
import './MultipleConsignorsModal.css';

const styles = {
  boldLabel: 'fw-bold',
  redLabel: 'text-danger fw-bold text-decoration-underline',
};

const CheckBanner = ({ currency, incoterms }) => {
  return (
    <div className="check-banner d-flex p-3 gap-4 mb-4">
      <img src={warningIcon} alt="Warning" className="check-banner-icon" width={45} height={45} />

      <div>
        <p className="m-0 fw-bold">CHECK</p>
        <p className="m-0">
          All invoices on this load <span className={styles.boldLabel}>MUST</span> have the same{' '}
          <span className={styles.boldLabel}>CURRENCY</span>: <span className={styles.redLabel}>{currency}</span> and{' '}
          <span className={styles.boldLabel}>INCOTERMS</span>: <span className={styles.redLabel}>{incoterms}</span>
        </p>
        <p className="m-0">Invoice changes must be added to the header.</p>
      </div>
    </div>
  );
};

const MultipleConsignorsModal = ({
  invoiceLines,
  consignorDetails,
  show = false,
  locked = false,
  onHide = () => {},
  onSave = () => {},
}) => {
  const store = useContext(StoreContext);
  const [initialOptions, setInitialOptions] = useState([]);
  const [uniqueInvoices, setUniqueInvoices] = useState([]);

  const refreshConsignorCodeDropdown = async (search, skip) => {
    if (!store.directionSelected[0]) return undefined;
    const options = await getNewLoadNeeNorDropdown(
      'neenor',
      store.directionSelected[0],
      'consignor',
      search,
      skip,
      store.consignorRPA[0]
    );

    return options;
  };

  // handles updating the consignor for a specific invoice, after a user has selected from the dropdown
  const updateConsignor = (invoiceNumber, consignor) => {
    setUniqueInvoices((prev) =>
      prev.map((item) =>
        item.invoiceNumber === invoiceNumber
          ? {
              ...item,
              companyName: consignor.data,
              neeNoridconsignor: String(consignor.id),
              addressDetailsComplete: !!consignor.addressDetailsComplete,
              eori: consignor.eori,
            }
          : item
      )
    );
  };

  useEffect(() => {
    const fetchInitialOptions = async () => {
      // Fetch initial consignor options once to avoid multiple API calls
      const opts = await refreshConsignorCodeDropdown('', 0);
      setInitialOptions(opts);
    };
    if (show && invoiceLines) {
      // Extract unique invoice numbers and their associated consignors from the excelState rows
      const uniqueInvoices = Array.from(
        new Map(
          invoiceLines?.map((row) => {
            const { invNumber, consignorId } = row;
            const consignorInfo = consignorDetails[consignorId] || {};

            return [
              invNumber,
              {
                invoiceNumber: invNumber,
                neeNoridconsignor: consignorId,
                eori: consignorInfo?.eori,
                addressDetailsComplete: !!consignorInfo?.addressDetailsComplete,
                companyName: consignorInfo?.companyName,
              },
            ];
          })
        ).values()
      );

      setUniqueInvoices(uniqueInvoices);

      if (!initialOptions.length) {
        fetchInitialOptions();
      }
    }
  }, [show, invoiceLines]);

  // used for adding currently selected option to the dropdown options
  const getAdditionalOptions = (item) => {
    return item.companyName ? [{ data: item.companyName, id: item.neeNoridconsignor }] : [];
  };

  // Determine the currency and incoterms to display in the banner based on the current store values
  let currency = 'Not available';
  let incoterms = 'Not available';
  if (show) {
    const currencyOption = store.currencyOptions[0]?.find((c) => String(c.id) === String(store.invoicecurr[0]));
    const incotermsOption = store.incotermsOptions[0]?.find((c) => String(c.id) === String(store.selectincoterm[0]));
    currency = currencyOption ? currencyOption.data : currency;
    incoterms = incotermsOption ? incotermsOption.data : incoterms;
  }

  return (
    <Modal className="qa-checklist-modal modal-height" animation={false} show={show} size="lg" centered onHide={onHide}>
      <Modal.Header>
        <div className="d-flex w-100 justify-content-between">
          <Modal.Title>Multiple Invoices - Consignors</Modal.Title>
          <div className="row pe-3">
            <div className="col cabie-company-info cci-header">
              <p className="mb-0 text-nowrap">Portal Ref</p>
              <p className="mb-0 text-nowrap">Customer Name</p>
              <p className="mb-0 text-nowrap">EORI Number</p>
            </div>
            <div className="col cabie-company-info">
              <p className="mb-0 text-nowrap">{store.portalRef[0] || '-'}</p>
              <p className="mb-0 text-nowrap">{store.clientIDname[0] || '-'}</p>
              <p className="mb-0 text-nowrap">{store.clientIDEori[0] || '-'}</p>
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <CheckBanner currency={currency} incoterms={incoterms} />

        {initialOptions.length > 0 ? (
          <DataTable
            dontRefreshPagination
            idKey="id"
            data={uniqueInvoices}
            className="table-striped"
            showAdvnSearch={false}
            sortable={false}
            filterable={false}
          >
            <DataTable.Row style={{ fontSize: '14px' }} />
            <DataTable.Column colKey="invoiceNumber" header="Invoice Number" className="align-middle" />
            <DataTable.Column colKey="neeNoridconsignor" header="Consignor">
              {(item) => (
                <div className="d-flex align-items-center gap-2">
                  <div style={{ width: 350 }}>
                    <NeeNorSelect
                      name={`consignor-${item.invoiceNumber}`}
                      dependencies={[store.directionSelected[0]]}
                      refresh={refreshConsignorCodeDropdown}
                      data={item.neeNoridconsignor}
                      onChangeAsObject={(option) => {
                        updateConsignor(item.invoiceNumber, option);
                      }}
                      initialOptions={initialOptions}
                      additionalOptions={getAdditionalOptions(item)}
                      disabled={locked}
                    />
                  </div>
                  <div>{item.eori}</div>
                  <div style={{ marginLeft: 'auto' }}>
                    {item.addressDetailsComplete === true && (
                      <img src={greenCircleTick} alt="Complete" width={20} height={20} />
                    )}
                    {item.addressDetailsComplete === false && (
                      <Tooltip icon={<img src={redCircleCross} alt="Incomplete" width={20} height={20} />}>
                        <p>{!item.neeNoridconsignor ? 'Missing consignor details' : 'Missing address details'}</p>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
            </DataTable.Column>
          </DataTable>
        ) : (
          <div className="d-flex justify-content-center align-items-center py-4">
            <LoadingSpinner size="2rem" />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <button className="epo-continue-button small-button" type="button" onClick={onHide}>
            {locked ? 'Close' : 'Cancel'}
          </button>
          {!locked && (
            <button
              className="epo-continue-button small-button"
              type="button"
              onClick={() => {
                onSave(uniqueInvoices);
                onHide();
              }}
            >
              Save
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MultipleConsignorsModal;
